// ── Keeping the keyboard inside a modal dialog ──────────────────────────
//
// aria-modal alone doesn't hold the keyboard. A dialog portalled to the end of <body> would let Tab
// from its last control leave the page, and Shift+Tab from its first reach the widget behind the
// backdrop. The Employee Performance dialogs call trapTab from their onKeyDown.

import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

/** What Tab can land on inside a dialog — the trap cycles through these. */
export const TABBABLE =
    'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'

/**
 * Sends Tab from the dialog's last tabbable element round to its first, and Shift+Tab from its first
 * (or from the dialog itself, which holds focus when it opens) round to its last. Anything else is
 * left to the browser.
 */
export function trapTab(e: ReactKeyboardEvent<HTMLElement>, dialog: HTMLElement | null): void {
    if (e.key !== 'Tab' || !dialog) return
    // Walked in document order and tested one by one: jsdom returns a selector list's matches
    // grouped by selector (every button, then every input), which would put the wrong one last.
    const items = [...dialog.querySelectorAll<HTMLElement>('*')].filter(el => el.matches(TABBABLE))
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    const at = document.activeElement
    if (e.shiftKey && (at === first || at === dialog)) {
        e.preventDefault()
        last.focus()
    } else if (!e.shiftKey && at === last) {
        e.preventDefault()
        first.focus()
    }
}
